import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  PLATFORM_LABELS,
  SUBMISSION_STATUS_LABELS,
  SUPPORT_BODY_MAX,
  SUPPORT_TICKET_CATEGORY_LABELS,
  submissionCheckMetaByPlan,
  supportTicketIsLive,
  type AdminSupportTicketDetailResponse,
  type Platform,
  type SupportRefSummary,
} from '@nexus/shared';

/** ": the post is on your connected account" — the check that stopped the run, as words. */
function stoppedLabel(planName: string | null): string {
  const meta = planName ? submissionCheckMetaByPlan(planName) : undefined;
  if (!meta) return '';
  const label = meta.label.charAt(0).toLowerCase() + meta.label.slice(1);
  return ` (stopped at: ${label})`;
}

import { ApiError, apiGet, apiSend } from '../../lib/api';
import { CheckList } from '../../components/CheckList';
import { RefCard } from '../SupportTicket';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmButton,
  ExternalLink,
  Fact,
  Facts,
  LoadErrorPage,
  LoadingPage,
  PageHeader,
  SelectField,
  TextField,
  When,
  statusLabel,
  statusTone,
} from '../../ui';

/**
 * One ticket, from the operator's side: the thread, the thing it is about,
 * and every button the ticket can drive.
 *
 * A SUBMISSION_REVIEW ticket about a rejected submission carries the two
 * re-review actions beside the submission itself: RE-RUN THE CHECKS for an
 * automatic rejection (the post is read again, now, as a fresh attempt) and
 * OVERTURN for a person's rejection (approve, with a note the creator reads).
 * The outcome of either is posted on the ticket by the system, so the
 * operator's next sentence can be about what happened rather than about it.
 */
export function AdminSupportTicket(): React.JSX.Element {
  const { id } = useParams();
  const [data, setData] = useState<AdminSupportTicketDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [overturnNote, setOverturnNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await apiGet<AdminSupportTicketDetailResponse>(`/admin/support/${id}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the ticket.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<string>): Promise<void> {
    setBusy(true);
    setActionError(null);
    setMessage(null);
    try {
      setMessage(await fn());
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const send = (): Promise<void> =>
    act(async () => {
      setData(await apiSend<AdminSupportTicketDetailResponse>('POST', `/admin/support/${id}/messages`, { body: reply.trim() }));
      setReply('');
      return 'Sent. The creator is told by email.';
    });

  const settle = (action: 'resolve' | 'close'): Promise<void> =>
    act(async () => {
      if (note.trim().length === 0) throw new ApiError(400, 'note_required', 'A resolution needs a note: the creator reads it.');
      setData(await apiSend<AdminSupportTicketDetailResponse>('POST', `/admin/support/${id}/${action}`, { note: note.trim() }));
      setNote('');
      return action === 'resolve' ? 'Resolved.' : 'Closed.';
    });

  const assign = (assignedToId: string): Promise<void> =>
    act(async () => {
      setData(
        await apiSend<AdminSupportTicketDetailResponse>('POST', `/admin/support/${id}/assign`, {
          assignedToId: assignedToId || null,
        }),
      );
      return assignedToId ? 'Assigned.' : 'Unassigned.';
    });

  const rerun = (): Promise<void> =>
    act(async () => {
      const body = await apiSend<AdminSupportTicketDetailResponse & { rerun: { status: string; sentence: string } }>(
        'POST',
        `/admin/support/${id}/rerun-checks`,
        {},
      );
      setData(body);
      return body.rerun.sentence;
    });

  const overturn = (): Promise<void> =>
    act(async () => {
      if (overturnNote.trim().length === 0) {
        throw new ApiError(400, 'note_required', 'An overturn needs a note: the creator reads it word for word.');
      }
      const body = await apiSend<AdminSupportTicketDetailResponse & { overturn: { sentence: string } }>(
        'POST',
        `/admin/support/${id}/overturn`,
        { note: overturnNote.trim() },
      );
      setData(body);
      setOverturnNote('');
      return body.overturn.sentence;
    });

  if (error && data === null) {
    return (
      <LoadErrorPage eyebrow="Operator" title="Ticket" heading="Could not load the ticket." onRetry={() => void load()}>
        <span>{error}</span>
      </LoadErrorPage>
    );
  }
  if (data === null) return <LoadingPage eyebrow="Operator" title="Ticket" />;

  const { ticket, messages, ref, operators } = data;
  const live = supportTicketIsLive(ticket.status);

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            Support <span aria-hidden="true">·</span> {SUPPORT_TICKET_CATEGORY_LABELS[ticket.category]}
          </>
        }
        title={ticket.subject}
        lead={`${ticket.creator.displayName ?? ticket.creator.email} · ${ticket.creator.email}`}
        actions={
          <>
            <Badge tone={statusTone(ticket.status, 'support')} testId="ticket-status">
              {statusLabel(ticket.status, 'support')}
            </Badge>
            <Link className="btn btn-ghost small" to="/admin/support">
              The queue
            </Link>
          </>
        }
      />

      {message && <Alert tone="positive">{message}</Alert>}
      {actionError && <Alert tone="critical">{actionError}</Alert>}

      <div className={ref ? 'split reference' : undefined}>
        <div className="stack">
          <Card title="The conversation" label="The conversation">
            <ol className="thread">
              {messages.map((m) => (
                <li key={m.id} data-role={m.authorRole}>
                  <div className="msg-head">
                    <span className="msg-author">{m.authorName}</span>
                    <When value={m.createdAt} withTime />
                    {m.authorRole !== 'CREATOR' && (
                      <span className="msg-unread muted">{m.readByCreatorAt ? 'Read by the creator' : 'Not read yet'}</span>
                    )}
                  </div>
                  <p className="msg-body">{m.body}</p>
                </li>
              ))}
            </ol>
          </Card>

          <Card title="Reply" sub="The creator is emailed that there is a reply; the words stay here.">
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <TextField
                label="Your reply"
                name="reply"
                textarea
                rows={4}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                maxLength={SUPPORT_BODY_MAX}
                disabled={ticket.status === 'CLOSED'}
              />
              <div className="actions">
                <Button type="submit" variant="primary" disabled={busy || ticket.status === 'CLOSED' || reply.trim().length === 0}>
                  {busy ? 'Sending…' : 'Send reply'}
                </Button>
              </div>
            </form>
          </Card>

          <Card title="Resolve or close" sub="Both need a note; it is the last message on the thread and the creator reads it.">
            <TextField
              label="Resolution note"
              name="note"
              textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={SUPPORT_BODY_MAX}
            />
            <div className="actions">
              <Button variant="primary" disabled={busy || !live || note.trim().length === 0} onClick={() => void settle('resolve')}>
                Resolve
              </Button>
              <ConfirmButton
                label="Close"
                title="Close this ticket?"
                question="Nobody can reply on a closed ticket. Resolve it instead if the creator might come back."
                confirmLabel="Close it"
                disabled={busy || ticket.status === 'CLOSED' || note.trim().length === 0}
                onConfirm={() => void settle('close')}
              />
            </div>
          </Card>

          <Card title="Assignment" tight>
            <SelectField
              label="Assigned to"
              name="assignedTo"
              value={ticket.assignedToId ?? ''}
              onChange={(e) => void assign(e.target.value)}
              disabled={busy}
            >
              <option value="">Nobody</option>
              {operators.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.email}
                </option>
              ))}
            </SelectField>
          </Card>
        </div>

        {ref && (
          <div className="stack">
            {ref.type === 'PostSubmission' ? (
              <SubmissionRef
                ref_={ref}
                busy={busy}
                overturnNote={overturnNote}
                setOverturnNote={setOverturnNote}
                onRerun={rerun}
                onOverturn={overturn}
              />
            ) : (
              <RefCard ref_={ref} />
            )}
          </div>
        )}
      </div>

      <Facts>
        <Fact label="Opened">
          <When value={ticket.createdAt} withTime />
        </Fact>
        <Fact label="Last activity">
          <When value={ticket.lastMessageAt} withTime />
        </Fact>
        <Fact label="Resolved">
          <When value={ticket.resolvedAt} withTime />
        </Fact>
        <Fact label="Creator status">
          <Badge tone={statusTone(ticket.creator.status, 'creator')}>{statusLabel(ticket.creator.status, 'creator')}</Badge>
        </Fact>
      </Facts>
    </>
  );
}

/**
 * The submission beside the thread, with the two buttons.
 *
 * Which button depends on WHO rejected it: a check gets a re-run, a person
 * gets an overturn. Both are offered only on a REJECTED row that has not
 * already been replaced.
 */
function SubmissionRef({
  ref_,
  busy,
  overturnNote,
  setOverturnNote,
  onRerun,
  onOverturn,
}: {
  ref_: Extract<SupportRefSummary, { type: 'PostSubmission' }>;
  busy: boolean;
  overturnNote: string;
  setOverturnNote: (v: string) => void;
  onRerun: () => Promise<void>;
  onOverturn: () => Promise<void>;
}): React.JSX.Element {
  const rejected = ref_.status === 'REJECTED' && !ref_.supersededBy;
  return (
    <Card
      title="The submission"
      eyebrow={
        <>
          {ref_.campaignName} <span aria-hidden="true">·</span> {PLATFORM_LABELS[ref_.platform as Platform]}
        </>
      }
      aside={<Badge tone={statusTone(ref_.status, 'submission')}>{SUBMISSION_STATUS_LABELS[ref_.status]}</Badge>}
    >
      <p>
        <ExternalLink href={ref_.postUrl} />
      </p>
      {ref_.rejectionReason && <Alert tone="critical">{ref_.rejectionReason}</Alert>}
      {ref_.holdReason && <Alert tone="waiting">{ref_.holdReason}</Alert>}
      {ref_.supersededBy && (
        <Alert tone="positive">
          Re-run: replaced by a fresh attempt. The outcome is on the thread; the new submission is in the{' '}
          <Link to="/admin/review">review queue</Link> if it passed.
        </Alert>
      )}

      <Facts>
        <Fact label="Submitted">
          <When value={ref_.submittedAt} withTime />
        </Fact>
        <Fact label="Rejected by">
          {ref_.rejectedBy === 'operator'
            ? 'An operator'
            : ref_.rejectedBy === 'automatic'
              ? /* The check's LABEL, never its plan name: `ACCOUNT_OWNERSHIP` is
                   a database word and this is a sentence. */
                `The automatic checks${stoppedLabel(ref_.stoppedAt)}`
              : '—'}
        </Fact>
      </Facts>

      <h3>The checks</h3>
      <CheckList checks={ref_.checks} stoppedAt={ref_.stoppedAt} />

      {rejected && ref_.rejectedBy === 'automatic' && (
        <div className="stack tight">
          <p className="muted">
            A check refused this on the day. Re-running reads the post again now and records a fresh
            attempt with its own checks; the creator sees the outcome on this ticket.
          </p>
          <div className="actions">
            <ConfirmButton
              label="Re-run the automatic checks"
              variant="primary"
              title="Re-run the checks?"
              question="The post is read again from the platform and a fresh submission is recorded. This one is superseded. If every check passes, the new one lands in the review queue."
              confirmLabel="Re-run them"
              disabled={busy}
              onConfirm={() => void onRerun()}
            />
          </div>
        </div>
      )}

      {rejected && ref_.rejectedBy === 'operator' && (
        <div className="stack tight">
          <p className="muted">
            A person rejected this. Overturning approves it, with a note the creator reads word for
            word; the checks are not re-run.
          </p>
          <TextField
            label="Overturn note"
            name="overturnNote"
            textarea
            rows={2}
            value={overturnNote}
            onChange={(e) => setOverturnNote(e.target.value)}
            maxLength={2000}
          />
          <div className="actions">
            <ConfirmButton
              label="Overturn the rejection"
              variant="primary"
              title="Overturn this rejection?"
              question="The submission becomes approved and its views count from here. The note is posted on the ticket."
              confirmLabel="Overturn it"
              disabled={busy || overturnNote.trim().length === 0}
              onConfirm={() => void onOverturn()}
            />
            <ConfirmButton
              label="Re-run the checks instead"
              title="Re-run the checks?"
              question="The post is read again and a fresh attempt is recorded; the operator's rejection stays on the old row."
              confirmLabel="Re-run them"
              disabled={busy}
              onConfirm={() => void onRerun()}
            />
          </div>
        </div>
      )}
    </Card>
  );
}
